import React from 'react';
import { Eye } from 'lucide-react';

interface SongEditorStoryNewProps {
  songData: any;
  onUpdate: (updates: any) => void;
}

export default function SongEditorStoryNew({ songData, onUpdate }: SongEditorStoryNewProps) {
  return (
    <div className="space-y-5 md:space-y-6">
      {/* Lyric Spotlight */}
      <section className="bg-[#1A1A1A] border border-[#282828] rounded-xl p-4 md:p-6 space-y-4 shadow-sm md:shadow-md">
        <div>
          <h3 className="text-[16px] font-semibold text-[#F5F5F5] mb-0.5">Lyric Spotlight</h3>
          <p className="text-[12px] text-[#808080]">
            A powerful lyric or hook that captures the song's essence
          </p>
        </div>

        <div>
          <textarea
            value={songData.lyric_spotlight || ''}
            onChange={(e) => onUpdate({ lyric_spotlight: e.target.value })}
            rows={3}
            className="w-full px-4 py-2.5 bg-[#0A0A0A] border border-[#282828] rounded-lg text-[14px] text-[#F5F5F5] resize-none focus:outline-none focus:border-[#40E0D0]/50"
            placeholder="Enter a memorable lyric from the song..."
            style={{ fontFamily: 'Inter, system-ui, -apple-system, sans-serif' }}
          />
        </div>
      </section>

      {/* Story Body */}
      <section className="bg-[#1A1A1A] border border-[#282828] rounded-xl p-4 md:p-6 space-y-4 shadow-sm md:shadow-md">
        <div>
          <h3 className="text-[16px] font-semibold text-[#F5F5F5] mb-0.5">Story Body</h3>
          <p className="text-[12px] text-[#808080]">
            The full story behind the song - its context, impact, and significance
          </p>
        </div>

        <div>
          <textarea
            value={songData.story_body || ''}
            onChange={(e) => onUpdate({ story_body: e.target.value })}
            rows={12}
            className="w-full px-4 py-2.5 bg-[#0A0A0A] border border-[#282828] rounded-lg text-[14px] text-[#F5F5F5] leading-relaxed resize-none focus:outline-none focus:border-[#40E0D0]/50"
            placeholder="Tell the story... What inspired this song? What was happening at the time? What impact did it have?"
            style={{ fontFamily: 'Inter, system-ui, -apple-system, sans-serif' }}
          />
        </div>
      </section>

      {/* Frontend Preview */}
      {(songData.lyric_spotlight || songData.story_body) && (
        <section className="bg-[#1A1A1A] border border-[#40E0D0]/30 rounded-xl p-4 md:p-6 space-y-4 shadow-sm md:shadow-md">
          <div className="flex items-center gap-2 mb-4">
            <Eye className="w-4 h-4 text-[#40E0D0]" />
            <h3 className="text-[16px] font-semibold text-[#40E0D0]">Frontend Preview</h3>
            <span className="text-[11px] text-[#808080]">How users will see this</span>
          </div>

          {songData.lyric_spotlight && (
            <div className="space-y-2">
              <div className="text-[12px] font-medium text-[#A0A0A0] uppercase tracking-wide">
                Lyric Spotlight
              </div>
              <div
                className="px-4 py-3 bg-[#0A0A0A] border border-[#282828] rounded-lg text-[15px] text-[#F5F5F5] leading-relaxed italic"
                style={{ fontFamily: 'Inter, system-ui, -apple-system, sans-serif' }}
              >
                "{songData.lyric_spotlight}"
              </div>
            </div>
          )}

          {songData.story_body && (
            <div className="space-y-2">
              <div className="text-[12px] font-medium text-[#A0A0A0] uppercase tracking-wide">
                The Story
              </div>
              <div
                className="px-4 py-3 bg-[#0A0A0A] border border-[#282828] rounded-lg text-[14px] text-[#E5E5E5] leading-relaxed whitespace-pre-wrap"
                style={{ fontFamily: 'Inter, system-ui, -apple-system, sans-serif', lineHeight: '1.7' }}
              >
                {songData.story_body}
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
